import MarkdownIt from 'markdown-it'
import { resolvePostPath } from '~/composables/usePermalink'
import type { Content } from '~/types/blog'

type MarkdownToken = ReturnType<MarkdownIt['parse']>[number]

type EmbeddedPostCardReferenceBase = {
  href: string
  key: string
}

export type EmbeddedPostCardReference = EmbeddedPostCardReferenceBase & (
  | { mode: 'id'; id: number }
  | { mode: 'slug'; slug: string }
)

export type EmbeddedPostCardState = {
  status: 'loading' | 'ready' | 'failed'
  content?: Content
}

export type EmbeddedPostCardMap = Record<string, EmbeddedPostCardState>

// getStandalonePostTarget 获取顶层独占段落中的文章链接或根路径
export const getStandalonePostTarget = (tokens: MarkdownToken[], index: number) => {
  const paragraphOpen = tokens[index]
  const inline = tokens[index + 1]
  const paragraphClose = tokens[index + 2]
  if (
    paragraphOpen?.type !== 'paragraph_open' ||
    paragraphOpen.level !== 0 ||
    paragraphOpen.hidden ||
    inline?.type !== 'inline' ||
    paragraphClose?.type !== 'paragraph_close' ||
    paragraphClose.hidden
  ) {
    return ''
  }

  const children = inline.children || []
  if (children.length === 1 && children[0]?.type === 'text') {
    const value = String(children[0].content || '').trim()
    return value.startsWith('/') && !value.startsWith('//') && !/\s/.test(value) ? value : ''
  }
  if (
    children.length < 3 ||
    children[0]?.type !== 'link_open' ||
    children[children.length - 1]?.type !== 'link_close' ||
    children.some(token => token.type === 'image') ||
    children.filter(token => token.type === 'link_open').length !== 1 ||
    children.filter(token => token.type === 'link_close').length !== 1
  ) {
    return ''
  }
  return String(children[0]?.attrGet('href') || '').trim()
}

// normalizeOrigins 规范化允许识别的站点来源
const normalizeOrigins = (origins: string[]) => {
  const result = new Set<string>()
  for (const value of origins) {
    try {
      if (value) {
        result.add(new URL(value).origin)
      }
    } catch {
      // 忽略未配置或无效的站点地址
    }
  }
  return result
}

// resolveEmbeddedPostCardReference 按当前文章固定链接规则解析站内引用
export const resolveEmbeddedPostCardReference = (
  href: string,
  fields: Record<string, unknown> | undefined,
  origins: string[] = []
): EmbeddedPostCardReference | null => {
  const value = String(href || '').trim()
  if (!value) {
    return null
  }

  let pathname = ''
  if (value.startsWith('/') && !value.startsWith('//')) {
    pathname = value
  } else if (/^https?:\/\//i.test(value)) {
    try {
      const parsed = new URL(value)
      if (!normalizeOrigins(origins).has(parsed.origin)) {
        return null
      }
      pathname = parsed.pathname
    } catch {
      return null
    }
  } else {
    return null
  }

  try {
    const resolved = resolvePostPath(fields, pathname)
    if (resolved?.mode === 'cid') {
      const id = Number(resolved.cid || 0)
      return id > 0 ? { href: value, key: `id:${id}`, mode: 'id', id } : null
    }
    if (resolved?.mode === 'slug') {
      const slug = String(resolved.slug || '').trim()
      return slug ? { href: value, key: `slug:${slug}`, mode: 'slug', slug } : null
    }
  } catch {
    return null
  }
  return null
}

// collectEmbeddedPostCardReferences 收集并去重 Markdown 中可转换的文章引用
export const collectEmbeddedPostCardReferences = (
  source: string,
  fields: Record<string, unknown> | undefined,
  origins: string[] = []
) => {
  const markdown = new MarkdownIt({ html: true, breaks: true, linkify: true, typographer: true })
  const tokens = markdown.parse(String(source || ''), {})
  const references = new Map<string, EmbeddedPostCardReference>()
  for (let index = 0; index < tokens.length - 2; index += 1) {
    const href = getStandalonePostTarget(tokens, index)
    if (!href) {
      continue
    }
    const reference = resolveEmbeddedPostCardReference(href, fields, origins)
    if (reference) {
      references.set(reference.href, reference)
    }
  }
  return [...references.values()]
}
